/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\pages\Dashboard.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

/**
SELLERIE MARINE - PAGE TABLEAU DE BORD

📋 DESCRIPTION:
Page React principale du tableau de bord avec vue d'ensemble de l'activité.
Interface de pilotage avec statistiques, graphiques et actions prioritaires.

🎯 RESPONSABILITÉS:
- Affichage des statistiques clés (devis, CA, taux conversion, clients)
- Visualisation graphique de la répartition des statuts de devis
- Liste des actions commerciales prioritaires avec urgence
- Historique des commandes fournisseurs récentes
- Tableau des devis récents avec accès rapide
- Système d'alertes pour notifications importantes
- Navigation rapide vers création nouveau devis

🔗 DÉPENDANCES:
- DashboardService: Agrégation données statistiques et métriques
- AuthContext: Contexte utilisateur pour personnalisation
- StatutsChart: Composant graphique répartition statuts
- ActionsCommerciales: Composant liste actions prioritaires
- HistoriqueCommandes: Composant historique commandes fournisseur
- React Router: Navigation vers autres sections

📡 INTERACTIONS:
- Services: Chargement données agrégées dashboard
- Composants: Intégration graphiques et listes spécialisées
- Navigation: Liens rapides vers devis, clients, paramètres
- Interface: Formatage localisé montants et dates
- Alertes: Système notifications pour actions urgentes

📊 STATUT: ACTIF (Production)
📅 Dernière mise à jour: Juin 2025
👤 Responsable: Équipe développement

💡 NOTES:
- Layout responsive en grille adaptative (1-4 colonnes)
- Formatage localisé des montants et dates (fr-FR)
- Système d'urgence actions commerciales (couleurs selon délais)
- Cohérence visuelle statuts avec page Devis
- Gestion d'erreurs avec interface de récupération
- Optimisation performance avec chargement asynchrone
- Interface scrollable pour sections longues (historique)
*/
import React, { useContext, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AuthContext from '../contexts/AuthContext';
import { dashboardService, DashboardStats, DevisRecentAvecClient, StatutsRepartition, ActionCommerciale } from '../services/DashboardService';
import StatutsChart from '../components/Dashboard/StatutsChart';
import ActionsCommerciales from '../components/Dashboard/ActionsCommerciales';
import HistoriqueCommandes from '../components/CommandeFournisseur/HistoriqueCommandes';
import BetaWelcome from '../components/Beta/BetaWelcome';

const Dashboard: React.FC = () => {
  const { user } = useContext(AuthContext);
  const [stats, setStats] = useState<DashboardStats>({
    totalDevis: 0,
    devisAcceptes: 0,
    devisEnAttente: 0,
    devisRefuses: 0,
    caTotal: 0,
    tauxConversion: 0,
    montantMoyen: 0,
    totalClients: 0,
  });
  const [devisRecents, setDevisRecents] = useState<DevisRecentAvecClient[]>([]);
  const [alertes, setAlertes] = useState<string[]>([]);
  const [statutsRepartition, setStatutsRepartition] = useState<StatutsRepartition>({ labels: [], data: [], colors: [] });
  const [actionsCommerciales, setActionsCommerciales] = useState<ActionCommerciale[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Charger les vraies données
  useEffect(() => {
    const chargerDonnees = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const donnees = await dashboardService.getDonneesDashboard();
        
        setStats(donnees.statistiques);
        setDevisRecents(donnees.devisRecents);
        setAlertes(donnees.alertes);
        setStatutsRepartition(donnees.statutsRepartition);
        setActionsCommerciales(donnees.actionsCommerciales);
      } catch (err) {
        console.error('Erreur lors du chargement des données du Dashboard:', err);
        setError('Erreur lors du chargement des données. Veuillez réessayer.');
      } finally {
        setLoading(false);
      }
    };

    chargerDonnees();
  }, []);

  // Fonction pour afficher le statut avec la bonne couleur (exactement comme la page Devis)
  const getStatusColor = (statut: string) => {
    switch (statut) {
      case 'NOUVEAU':
        return 'bg-gray-100 text-gray-600'; // Gris clair #9CA3AF
      case 'BROUILLON':
        return 'bg-gray-200 text-gray-700'; // Gris foncé #6B7280
      case 'ENREGISTRE':
        return 'bg-blue-100 text-blue-700'; // Bleu #3B82F6
      case 'COMPLET':
        return 'bg-green-100 text-green-700'; // Vert #10B981
      case 'EN_ATTENTE_RETOUR_CLIENT':
        return 'bg-orange-100 text-orange-700'; // Orange #F59E0B
      case 'ACCEPTE':
        return 'bg-blue-200 text-blue-800'; // Bleu foncé #2563EB
      case 'TERMINE_LIVRE':
        return 'bg-purple-100 text-purple-700'; // Violet #8B5CF6
      case 'ANNULE':
        return 'bg-red-100 text-red-700'; // Rouge #EF4444
      case 'ABANDONNE':
        return 'bg-yellow-100 text-yellow-700'; // Jaune #F59E0B
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Fonction pour afficher le nom du statut avec emoji (exactement comme la page Devis)
  const getStatusLabel = (statut: string) => {
    switch (statut) {
      case 'NOUVEAU':
        return '📄 Nouveau';
      case 'BROUILLON':
        return '📝 Brouillon';
      case 'ENREGISTRE':
        return '💾 Enregistré';
      case 'COMPLET':
        return '✅ Complet';
      case 'EN_ATTENTE_RETOUR_CLIENT':
        return '⏳ En attente retour client';
      case 'ACCEPTE':
        return '✍️ Accepté par le client';
      case 'TERMINE_LIVRE':
        return '🎉 Terminé / Livré';
      case 'ANNULE':
        return '❌ Annulé';
      case 'ABANDONNE':
        return '🚫 Abandonné';
      default:
        return statut;
    }
  };

  // Fonction pour formater les montants en euros
  const formatMontant = (montant: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(montant);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des données...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <svg className="h-12 w-12 text-red-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833-.23 2.5 1.732 2.5z" />
            </svg>
            <h3 className="text-lg font-medium text-red-800 mb-2">Erreur de chargement</h3>
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="btn-primary"
            >
              Réessayer
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">Tableau de bord</h1>
        <Link
          to="/devis/nouveau"
          className="btn-primary flex items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 mr-2"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
              clipRule="evenodd"
            />
          </svg>
          Nouveau devis
        </Link>
      </div>

      {/* Message de bienvenue pour les bêta-testeurs */}
      {user?.isBeta && <BetaWelcome />}

      {/* Alertes */}
      {alertes.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <h3 className="text-sm font-medium text-yellow-800">Alertes</h3>
          </div>
          <div className="mt-2">
            <ul className="text-sm text-yellow-700 space-y-1">
              {alertes.map((alerte, index) => (
                <li key={index}>• {alerte}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <h2 className="text-lg font-medium text-gray-900 mb-2">Devis</h2>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary-600">{stats.totalDevis}</div>
              <div className="text-sm text-gray-500">Total</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{stats.devisAcceptes}</div>
              <div className="text-sm text-gray-500">Acceptés</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">{stats.devisEnAttente}</div>
              <div className="text-sm text-gray-500">En attente</div>
            </div>
          </div>
        </div>

        <div className="card">
          <h2 className="text-lg font-medium text-gray-900 mb-2">Chiffre d'affaires</h2>
          <div className="text-3xl font-bold text-primary-600">{formatMontant(stats.caTotal)}</div>
          <div className="text-sm text-gray-500 mt-1">Total des devis acceptés</div>
        </div>

        <div className="card">
          <h2 className="text-lg font-medium text-gray-900 mb-2">Taux de conversion</h2>
          <div className="text-3xl font-bold text-primary-600">{stats.tauxConversion}%</div>
          <div className="text-sm text-gray-500 mt-1">Devis acceptés / Total</div>
        </div>

        <div className="card">
          <h2 className="text-lg font-medium text-gray-900 mb-2">Clients</h2>
          <div className="text-3xl font-bold text-primary-600">{stats.totalClients}</div>
          <div className="text-sm text-gray-500 mt-1">Total clients</div>
          <div className="text-sm text-gray-500 mt-1">
            Montant moyen: {formatMontant(stats.montantMoyen)}
          </div>
        </div>
      </div>

      {/* Layout optimisé en 2 colonnes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Colonne gauche : Graphique */}
        <div className="card">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Répartition des statuts</h2>
          <StatutsChart data={statutsRepartition} />
        </div>

        {/* Colonne droite : Actions commerciales */}
        <div className="card">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-lg font-medium text-gray-900">Actions à effectuer</h2>
              <p className="text-xs text-gray-500 mt-1">
                Urgence basée sur les jours d'attente : 
                <span className="ml-1 text-yellow-600">Faible (3-7j)</span> • 
                <span className="ml-1 text-orange-600">Moyenne (7-14j)</span> • 
                <span className="ml-1 text-red-600">Haute (+14j)</span>
              </p>
            </div>
            {actionsCommerciales.length > 0 && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                {actionsCommerciales.length} action{actionsCommerciales.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            <ActionsCommerciales actions={actionsCommerciales} />
          </div>
        </div>
      </div>

      {/* Historique des commandes fournisseurs */}
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-gray-900">Historique des commandes fournisseurs</h2>
          <Link
            to="/commandes-fournisseur"
            className="text-primary-600 hover:text-primary-800 text-sm font-medium"
          >
            Voir l'historique complet
          </Link>
        </div>
        <HistoriqueCommandes />
      </div>

      {/* Devis récents */}
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-gray-900">Devis récents</h2>
          <Link to="/devis" className="text-primary-600 hover:text-primary-800 text-sm font-medium">
            Voir tous les devis
          </Link>
        </div>
        {devisRecents.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Référence
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {devisRecents.map((devis) => (
                  <tr key={devis.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {devis.reference}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>
                        <div className="font-medium">{devis.clientNom}</div>
                        {devis.clientSociete && (
                          <div className="text-xs text-gray-400">{devis.clientSociete}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {devis.date}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatMontant(devis.montant)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(devis.statut)}`}>
                        {getStatusLabel(devis.statut)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Link to={`/devis/${devis.id}`} className="text-primary-600 hover:text-primary-900 mr-4">
                        Voir
                      </Link>
                      <Link to={`/devis/${devis.id}/edit`} className="text-primary-600 hover:text-primary-900">
                        Modifier
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun devis</h3>
            <p className="mt-1 text-sm text-gray-500">Commencez par créer votre premier devis.</p>
            <div className="mt-6">
              <Link
                to="/devis/nouveau"
                className="btn-primary"
              >
                Créer un devis
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
